import { fetchApi } from '../lib/apiClient';
import { Character, CreateCharacterRequest, UpdateCharacterRequest } from '../types/character';

/**
 * 获取所有角色卡
 * @returns 角色卡列表的Promise
 */
export const fetchCharacters = async (): Promise<Character[]> => {
  try {
    const data = await fetchApi<Character[]>('/characters', {
      method: 'GET',
    });

    if (!data || !Array.isArray(data)) {
      console.error('Invalid response format for characters:', data);
      return []; // 返回空数组作为默认值
    }

    return data;
  } catch (error) {
    console.error('Error in fetchCharacters:', error);
    throw error;
  }
};

/**
 * 获取特定角色卡
 * @param id 角色卡ID
 * @returns 角色卡的Promise
 */
export const fetchCharacterById = async (id: string): Promise<Character> => {
  try {
    const data = await fetchApi<Character>(`/characters/${id}`, {
      method: 'GET',
    });

    if (!data || !data.id) {
      throw new Error(`Character with id ${id} not found`);
    }

    return data;
  } catch (error) {
    console.error(`Error in fetchCharacterById for id ${id}:`, error);
    throw error;
  }
};

/**
 * 按类型获取角色卡
 * @param type 角色卡类型
 * @returns 角色卡列表的Promise
 */
export const fetchCharactersByType = async (type: 'my' | 'official' | 'public'): Promise<Character[]> => {
  try {
    let endpoint = '/characters';

    switch (type) {
      case 'my':
        endpoint = '/characters/my';
        break;
      case 'official':
        endpoint = '/characters/official';
        break;
      case 'public':
        endpoint = '/characters/public';
        break;
    }

    const data = await fetchApi<Character[]>(endpoint, {
      method: 'GET',
    });

    if (!data || !Array.isArray(data)) {
      console.error(`Invalid response format for ${type} characters:`, data);
      return [];
    }

    return data;
  } catch (error) {
    console.error(`Error in fetchCharactersByType for type ${type}:`, error);
    throw error;
  }
};

/**
 * 创建角色卡
 * @param data 角色卡数据
 * @returns 创建的角色卡Promise
 */
export const createCharacter = async (data: CreateCharacterRequest): Promise<Character> => {
  try {
    const character = await fetchApi<Character>('/characters', {
      method: 'POST',
      body: data,
    });

    if (!character || !character.id) {
      throw new Error('Invalid response from create character API');
    }

    return character;
  } catch (error) {
    console.error('Error in createCharacter:', error);
    throw error;
  }
};

/**
 * 更新角色卡
 * @param id 角色卡ID
 * @param data 更新数据
 * @returns 更新后的角色卡Promise
 */
export const updateCharacter = async (id: string, data: UpdateCharacterRequest): Promise<Character> => {
  try {
    const character = await fetchApi<Character>(`/characters/${id}`, {
      method: 'PUT',
      body: data,
    });

    if (!character || !character.id) {
      throw new Error('Invalid response from update character API');
    }

    return character;
  } catch (error) {
    console.error(`Error in updateCharacter for id ${id}:`, error);
    throw error;
  }
};

/**
 * 删除角色卡
 * @param id 角色卡ID
 * @returns 删除结果Promise
 */
export const deleteCharacter = async (id: string): Promise<void> => {
  try {
    await fetchApi(`/characters/${id}`, {
      method: 'DELETE',
    });
  } catch (error) {
    console.error(`Error in deleteCharacter for id ${id}:`, error);
    throw error;
  }
};

/**
 * 发布到市场
 * @param id 角色卡ID
 * @param allowPromptVisible 是否允许查看提示词
 * @returns 更新后的角色卡Promise
 */
export const publishToMarket = async (id: string, allowPromptVisible: boolean = false): Promise<Character> => {
  try {
    return await updateCharacter(id, {
      isPublic: true,
      allowPromptVisible,
    });
  } catch (error) {
    console.error(`Error in publishToMarket for id ${id}:`, error);
    throw error;
  }
};

/**
 * 撤回审核
 * @param id 角色卡ID
 * @returns 更新后的角色卡Promise
 */
export const withdrawFromMarket = async (id: string): Promise<Character> => {
  try {
    return await updateCharacter(id, {
      isPublic: false,
    });
  } catch (error) {
    console.error(`Error in withdrawFromMarket for id ${id}:`, error);
    throw error;
  }
};

/**
 * 下架角色卡
 * @param id 角色卡ID
 * @returns 更新后的角色卡Promise
 */
export const unpublishCharacter = async (id: string): Promise<Character> => {
  try {
    return await updateCharacter(id, {
      isPublic: false,
    });
  } catch (error) {
    console.error(`Error in unpublishCharacter for id ${id}:`, error);
    throw error;
  }
};

/**
 * 更新角色卡使用统计
 * @param id 角色卡ID
 * @param type 统计类型
 */
export const updateCharacterUsage = async (id: string, type: 'usage' | 'message'): Promise<void> => {
  try {
    await fetchApi(`/characters/${id}/usage`, {
      method: 'POST',
      body: { type },
    });
  } catch (error) {
    console.error(`Error in updateCharacterUsage for id ${id}:`, error);
    // 不抛出错误，避免影响主要功能
  }
};